import React, { createContext, useCallback, useContext, useRef } from 'react';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('WiseboardRefreshContext', 'wiseboard');

type RefreshFn = () => void | Promise<void>;

interface WiseboardRefreshContextValue {
  registerRefresh: (fn: RefreshFn) => () => void;
  refreshAll: () => Promise<void>;
}

const WiseboardRefreshContext = createContext<WiseboardRefreshContextValue | null>(null);

interface Props {
  children: React.ReactNode;
}

export function WiseboardRefreshProvider({ children }: Props) {
  const fetchersRef = useRef<Set<RefreshFn>>(new Set());

  const registerRefresh = useCallback((fn: RefreshFn) => {
    fetchersRef.current.add(fn);
    return () => {
      fetchersRef.current.delete(fn);
    };
  }, []);

  const refreshAll = useCallback(async () => {
    const fetchers = Array.from(fetchersRef.current);
    log.info('Refreshing all Wiseboard panels', { count: fetchers.length });
    // Defer each fetcher to a microtask so synchronous throws become rejections
    // that Promise.allSettled can capture, instead of bubbling out.
    await Promise.allSettled(fetchers.map((fn) => Promise.resolve().then(fn)));
  }, []);

  const value: WiseboardRefreshContextValue = { registerRefresh, refreshAll };

  return (
    <WiseboardRefreshContext.Provider value={value}>
      {children}
    </WiseboardRefreshContext.Provider>
  );
}

export function useWiseboardRefresh(): WiseboardRefreshContextValue {
  const ctx = useContext(WiseboardRefreshContext);
  if (!ctx) {
    throw new Error('useWiseboardRefresh must be used within a WiseboardRefreshProvider');
  }
  return ctx;
}

export default WiseboardRefreshContext;
